import React, { useState, useEffect } from "react";
import { Dialog } from "@material-ui/core";
import type { StateService } from "@uirouter/angularjs";
import type { IModalService } from "App/Modules/Services";
import type { IAlarmCentralsRepository } from "App/Modules/Repositories";
import type { IAlarmCentral, IOperatorStationSlim, IOperatorSlim, ISiteSlim } from "App/Modules/Models";

import { AlarmCentralIntro } from "./AlarmCentralIntro";
import { SitesLinkList } from "./SitesLinkList";
import { OperatorStationsLinkList } from "./OperatorStationsLinkList";

import { jsonEqual } from "../../Shared";

import { EditAlarmCentralDialog } from "./Dialogs/EditAlarmCentralDialog";
import "./_alarm-central.scss";

type InjectedProps = {
    $state: StateService;
    mmAlarmCentralsRepository: IAlarmCentralsRepository;
    mmModalService: IModalService;
};

type AlarmCentalDetailsProps = InjectedProps;

export const AlarmCentralDetails = (props: AlarmCentalDetailsProps) => {
    const { alarmCentralId } = props.$state.params;

    const [isAlarmCentralLoading, setIsAlarmCentralLoading] = useState(true);
    const [isOperatorsLoading, setIsOperatorsLoading] = useState(true);
    const [isOperatorStationsLoading, setIsOperatorStationsLoading] = useState(true);
    const [isSitesLoading, setIsSitesLoading] = useState(true);

    const [isEditAlarmCentralDialogOpen, setIsEditAlarmCentralDialogOpen] = useState(false);

    const [alarmCentral, setAlarmCentral] = useState<IAlarmCentral>();
    const [operatorStations, setOperatorStations] = useState<IOperatorStationSlim[]>();
    const [operators, setOperators] = useState<IOperatorSlim[]>();
    const [sites, setSites] = useState<ISiteSlim[]>();

    const getAlarmCentral = () => {
        props.mmAlarmCentralsRepository.getAlarmCentral(alarmCentralId).subscribe(
            (response) => {
                if (!jsonEqual(response, alarmCentral)) {
                    setAlarmCentral(response.data);
                    setIsAlarmCentralLoading(false);
                }
            },
            (error) => {
                setAlarmCentral(error);
                setIsAlarmCentralLoading(false);
            },
        );
    };
    const getOperators = () => {
        props.mmAlarmCentralsRepository.getOperators(alarmCentralId).subscribe(
            (response) => {
                if (!jsonEqual(response, operators)) {
                    setOperators(response.data);
                    setIsOperatorsLoading(false);
                }
            },
            (error) => {
                setOperators(error);
                setIsOperatorsLoading(false);
            },
        );
    };
    const getOperatorStations = () => {
        props.mmAlarmCentralsRepository.getOperatorStationsForAlarmCentral(alarmCentralId).subscribe(
            (response) => {
                if (!jsonEqual(response, operatorStations)) {
                    setOperatorStations(response.data);
                    setIsOperatorStationsLoading(false);
                }
            },
            (error) => {
                setOperatorStations(error);
                setIsOperatorStationsLoading(false);
            },
        );
    };
    const getSites = () => {
        props.mmAlarmCentralsRepository.getSites(alarmCentralId).subscribe(
            (response) => {
                if (!jsonEqual(response, sites)) {
                    setSites(response.data);
                    setIsSitesLoading(false);
                }
            },
            (error) => {
                setSites(error);
                setIsSitesLoading(false);
            },
        );
    };
    useEffect(() => {
        if (!alarmCentral) {
            getAlarmCentral();
            getOperators();
            getOperatorStations();
            getSites();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alarmCentral]);

    const handleEditAlarmCentral = () => {
        setIsAlarmCentralLoading(true);
        props.mmAlarmCentralsRepository.getAlarmCentral(alarmCentralId).subscribe(
            (response) => {
                if (!jsonEqual(response, alarmCentral)) {
                    setAlarmCentral(response.data);
                    setIsAlarmCentralLoading(false);
                }
            },
            (error) => {
                setAlarmCentral(error);
                setIsAlarmCentralLoading(false);
            },
        );
    };

    const addNewOperatorStation = (event) => {
        event.preventDefault();
        props.mmModalService.open("mmAddOperatorStationToAlarmCentral", null, alarmCentral).subscribe((response) => {
            setOperatorStations(operatorStations.concat(response));
        });
    };
    const editOperatorStation = (operatorStation: IOperatorSlim) => {
        props.mmModalService.open("irEditOperatorStation", null, operatorStation).subscribe((response) => {
            if (response === null) {
                setOperatorStations(operatorStations.filter((item) => item.id !== operatorStation.id));
            }
            // edit has not been implemented yet according to the old code
        });
    };

    const handleEditAlarmCentralDialogClose = () => {
        setIsEditAlarmCentralDialogOpen(false);
    };

    const isLoading = () => !(alarmCentral && operatorStations && operators && sites);

    return (
        <div className="alarm-central">
            <div className="alarm-central__panel">
                <AlarmCentralIntro
                    alarmCentral={alarmCentral}
                    operators={operators}
                    isLoading={isLoading()}
                    isAlarmCentralLoading={isAlarmCentralLoading}
                    isOperatorsLoading={isOperatorsLoading}
                    isEditAlarmCentralDialogOpen={setIsEditAlarmCentralDialogOpen}
                />
            </div>
            <div className="alarm-central__panel alarm-central__panel--spacing alarm-central__panel--half">
                <SitesLinkList sites={sites} isLoading={isLoading()} isSitesLoading={isSitesLoading} />
            </div>

            <div className="alarm-central__panel alarm-central__panel--spacing alarm-central__panel--half">
                <OperatorStationsLinkList
                    operatorStations={operatorStations}
                    addNewOperatorStation={addNewOperatorStation}
                    editOperatorStation={editOperatorStation}
                    isLoading={isLoading()}
                    isOperatorStationsLoading={isOperatorStationsLoading}
                />
            </div>
            {!isLoading() ? (
                <React.Fragment>
                    <Dialog
                        onClose={handleEditAlarmCentralDialogClose}
                        open={isEditAlarmCentralDialogOpen}
                        fullWidth
                        maxWidth="sm"
                    >
                        <EditAlarmCentralDialog
                            onClose={handleEditAlarmCentralDialogClose}
                            alarmCentral={alarmCentral}
                            alarmCentralRepository={props.mmAlarmCentralsRepository}
                            onEditAlarmCentralChange={() => handleEditAlarmCentral()}
                        />
                    </Dialog>
                </React.Fragment>
            ) : null}
        </div>
    );
};
